import {
  kitPartCss,
  panel,
  panelAction,
  panelActionCss,
  panelCss,
  rowList,
  rowsCss,
} from '../armKit';
import { searchPath } from '../destinations';
import { deepWorkFacts } from '../pageData';
import { viewedUser } from '../profileFacts';
import { EDGE, INK, MUTED, surfacePartCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

type Hit = {
  workId: number;
  title: string;
  url: string | null;
  sentence: string | null;
};

const MIN_CARDS = 10;
const FLAG = 'corpussearch';
const INPUT_CLASS = 'profile-liautotest-corpussearch__input';
const RESULTS_CLASS = 'profile-liautotest-corpussearch__results';
const TYPED_SOURCE = 'profile-liautotest-search-typed';
const HIT_SOURCE = 'profile-liautotest-search-hit';
const SITE_SOURCE = 'profile-liautotest-search-site';

function armCss(scope: string, searching: string): string {
  return (
    panelCss(scope, { frame: 'flush', marginBottom: '0' }) +
    rowsCss(scope, {
      marginTop: '4px',
      itemPadding: '10px 0',
      metaOnPhone: true,
    }) +
    // The matched sentence rides the meta slot, which the kit styles as a
    // nowrap end-of-row figure; here it is prose, so the row wraps and the
    // sentence takes a full-width line under the title.
    kitPartCss('row', scope, 'flex-wrap: wrap;') +
    kitPartCss(
      'rowMeta',
      scope,
      'flex: 1 1 100%; white-space: normal; line-height: 1.4;',
    ) +
    panelActionCss(scope) +
    `${scope} .${INPUT_CLASS} {
  display: block;
  width: 100%;
  padding: 9px 14px;
  font-size: 14px;
  line-height: 1.4;
  color: ${INK};
  background: #fff;
  border: 1px solid ${EDGE};
  border-radius: 6px;
}
${scope} .${INPUT_CLASS}::placeholder {
  color: ${MUTED};
}
` +
    // The PANE alone, never the uploads container: the section nav stays, and
    // the cards are hidden rather than removed, so the served Download anchors
    // and the paginator both survive a search.
    surfacePartCss('pane', searching, 'display: none;')
  );
}

// "Search Ada's uploads", never "all of": the catalog is a capped fetch, so
// on the biggest profiles the box searches a prefix and "all" would promise a
// coverage it does not have. No figure either — no count on this page is
// bulletproof — and "uploads" rather than "papers", because the corpus holds
// book chapters and more, and it is the word the header over the box uses.
function boxLabel(): string {
  const name = viewedUser()?.first_name?.trim();

  return name == null || name === ''
    ? 'Search uploads'
    : `Search ${name}'s uploads`;
}

// armKit's firstSentences sentence model, shared so the fallback line and the
// matched line cannot disagree about where a sentence ends: a run up to .!? is
// a sentence, and text with no terminator is one sentence whole.
function sentencesOf(text: string): string[] {
  const trimmed = text.trim();
  if (trimmed === '') return [];

  const matched = trimmed.match(/[^.!?]+[.!?]+(\s|$)/g);

  return (matched ?? [trimmed]).map((sentence) => sentence.trim());
}

// The sentence containing the first match; for a title-only hit — or a match
// no single sentence contains — the summary's first sentence, or nothing.
function sentenceFor(summary: string, needle: string): string | null {
  const sentences = sentencesOf(summary);

  return (
    sentences.find((sentence) => sentence.toLowerCase().includes(needle)) ??
    sentences[0] ??
    null
  );
}

// Map order is the catalog's own order — the author's. Never re-ranked.
function hitsFor(catalog: Map<number, WorkFacts>, query: string): Hit[] {
  const needle = query.toLowerCase();

  return [...catalog].flatMap(([workId, facts]) => {
    const title = facts.title?.trim() ?? '';
    const summary = facts.summary ?? '';
    const matched =
      title.toLowerCase().includes(needle) ||
      summary.toLowerCase().includes(needle);
    if (title === '' || !matched) return [];

    return [
      { workId, title, url: facts.url, sentence: sentenceFor(summary, needle) },
    ];
  });
}

function searchInput(): HTMLInputElement {
  const label = boxLabel();
  const input = document.createElement('input');
  input.type = 'search';
  input.className = INPUT_CLASS;
  input.placeholder = label;
  input.setAttribute('aria-label', label);

  return input;
}

function mountSearch(ctx: ArmContext, slot: HTMLElement): void {
  const { root, body } = panel();
  const input = searchInput();
  const results = document.createElement('div');
  results.className = RESULTS_CLASS;

  let catalog: Map<number, WorkFacts> | null = null;
  let requested = false;
  let typed = false;

  const renderResults = (query: string): void => {
    results.textContent = '';
    // Pending mid-typing shows nothing rather than "no matches"; the arrival
    // callback below re-runs the query the moment the catalog lands.
    if (catalog == null) return;

    const hits = hitsFor(catalog, query);
    if (hits.length === 0) {
      results.appendChild(
        panelAction(
          `Search all of Academia for '${query}'`,
          searchPath(query),
          () => ctx.track(SITE_SOURCE),
        ),
      );
      return;
    }

    results.appendChild(
      rowList(
        hits.map((hit) => ({
          title: hit.title,
          url: hit.url,
          meta: hit.sentence ?? undefined,
          onClick: () => ctx.track(HIT_SOURCE, { entityId: hit.workId }),
        })),
      ),
    );
  };

  const update = (): void => {
    const query = input.value.trim();
    if (query === '') {
      ctx.setSurfaceFlag(FLAG, { value: 'off' });
      results.textContent = '';
      return;
    }

    if (!typed) {
      typed = true;
      ctx.track(TYPED_SOURCE);
    }

    ctx.setSurfaceFlag(FLAG, { value: 'on' });
    renderResults(query);
  };

  // Lazy on purpose: a visitor who never touches the box costs no request.
  // deepWorkFacts settles no matter what came over the wire, so the then is
  // safe unchained.
  const load = (): void => {
    if (requested) return;
    requested = true;

    void deepWorkFacts(ctx.viewedUserId).then((facts) => {
      catalog = facts;
      update();
    });
  };

  input.addEventListener('focus', load);
  input.addEventListener('input', () => {
    load();
    update();
  });

  body.appendChild(input);
  body.appendChild(results);
  slot.appendChild(root);
}

export const corpusSearch: ArmSpec = {
  stamp: (ctx) => ({
    render_state: ctx.cards().length >= MIN_CARDS ? 'rendered' : 'fallback',
  }),

  onReady: (ctx) => {
    if (ctx.cards().length < MIN_CARDS) return;

    // listStart puts the box under the Uploads header and the section chips,
    // just above the first card — and OUTSIDE the pane, which matters: the
    // hide rule above blanks the pane while a query is live, and the box must
    // not vanish with it.
    const slot = ctx.mountSlot('listStart');
    if (slot == null) return;

    // Stamped 'on' once to learn the selector the hide rule keys on, then
    // rested 'off' before any sheet exists to read it — nothing paints between.
    const searching = ctx.setSurfaceFlag(FLAG, { value: 'on' });
    ctx.setSurfaceFlag(FLAG, { value: 'off' });
    if (searching == null) return;

    ctx.injectStyles(armCss(ctx.scope, `${ctx.scope}${searching}`));
    mountSearch(ctx, slot);
    // No autofocus anywhere above: nothing may move the viewport.
    ctx.markRendered();
  },
};
